import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatMontant } from '../../utils/format';
import { t } from '../../i18n';
import { Upload, Star, Trash2, Edit, CheckCircle, ImagePlus, Camera, X, RefreshCw, ZoomIn } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import { Button } from '../../components/common/Input';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

// ── Détection mobile ──────────────────────────────────────────────────────────
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// ── Modal Webcam (desktop) ────────────────────────────────────────────────────
function ModalWebcam({ onCapture, onClose }) {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const streamRef   = useRef(null);
  const [ready, setReady]         = useState(false);
  const [captured, setCaptured]   = useState(null); // dataURL de l'aperçu capturé
  const [facingBack, setFacingBack] = useState(true);
  const [erreur, setErreur]       = useState(null);

  const demarrerCamera = useCallback(async (back = true) => {
    // Arrêter le flux précédent
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setCaptured(null);
    setReady(false);
    setErreur(null);
    try {
      const constraints = {
        video: {
          facingMode: back ? 'environment' : 'user',
          width: { ideal: 1280 },
          height: { ideal: 960 },
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setReady(true);
      }
    } catch (err) {
      setErreur('Impossible d\'accéder à la caméra. Vérifiez les permissions du navigateur.');
    }
  }, []);

  useEffect(() => {
    demarrerCamera(true);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [demarrerCamera]);

  const capturer = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL('image/jpeg', 0.92));
  };

  const retake = () => {
    setCaptured(null);
    demarrerCamera(facingBack);
  };

  const confirmer = () => {
    if (!captured) return;
    // Convertir dataURL → File
    const arr  = captured.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8  = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    const file = new File([new Blob([u8], { type: mime })], `capture_${Date.now()}.jpg`, { type: mime });
    onCapture([file]);
    onClose();
  };

  const basculerCamera = () => {
    const next = !facingBack;
    setFacingBack(next);
    demarrerCamera(next);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg overflow-hidden">
        {/* En-tête */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Camera size={16} className="text-yellow-400" /> Prendre une photo
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        {/* Corps */}
        <div className="relative bg-black" style={{ aspectRatio: '4/3' }}>
          {erreur ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
              <Camera size={36} className="text-gray-600" />
              <p className="text-red-400 text-sm">{erreur}</p>
              <button onClick={() => demarrerCamera(facingBack)}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-black font-medium rounded-lg text-sm">
                Réessayer
              </button>
            </div>
          ) : captured ? (
            <img src={captured} alt="Aperçu" className="w-full h-full object-contain" />
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {/* Grille viseur */}
              {ready && (
                <div className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)',
                    backgroundSize: '33.33% 33.33%'
                  }}
                />
              )}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />

          {/* Bouton flip caméra */}
          {!captured && !erreur && (
            <button
              onClick={basculerCamera}
              className="absolute top-3 right-3 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              title="Changer de caméra"
            >
              <RefreshCw size={16} />
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-4 px-4 py-4">
          {captured ? (
            <>
              <button onClick={retake}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
                <RefreshCw size={14} /> Reprendre
              </button>
              <button onClick={confirmer}
                className="flex-1 py-2.5 bg-yellow-600 hover:bg-yellow-500 text-black rounded-xl text-sm font-bold flex items-center justify-center gap-2">
                <CheckCircle size={14} /> Utiliser cette photo
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm font-medium">
                Annuler
              </button>
              <button
                onClick={capturer}
                disabled={!ready}
                className="w-16 h-16 rounded-full bg-white disabled:bg-gray-600 hover:bg-gray-200 border-4 border-yellow-500 flex items-center justify-center transition-all disabled:border-gray-500"
                title="Capturer"
              >
                <div className="w-12 h-12 rounded-full bg-white disabled:bg-gray-600" />
              </button>
              <div className="px-5 py-2.5 opacity-0 select-none text-sm">Annuler</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DetailProduit({ produit, onClose, onRefresh }) {
  const [uploading, setUploading]     = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [dragOver, setDragOver]       = useState(false);
  const [webcamOpen, setWebcamOpen]   = useState(false);
  const [photoZoom, setPhotoZoom]     = useState(null); // URL de la photo agrandie
  const [seuilEdit, setSeuilEdit]     = useState(null);
  const [seuilVal, setSeuilVal]       = useState('');
  const fileInputRef   = useRef(null);
  const cameraInputRef = useRef(null); // input capture pour mobile

  // ── Upload (accepte un tableau de File) ──────────────────────────────────
  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const valides = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (valides.length === 0) return toast.error('Seules les images sont acceptées');
    if (valides.length > 10) return toast.error('Maximum 10 photos à la fois');

    setUploading(true);
    setUploadCount(0);
    setUploadTotal(valides.length);

    const fd = new FormData();
    valides.forEach(f => fd.append('photos', f));

    try {
      const { data } = await api.post(`/produits/${produit.id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadCount(Math.round((e.loaded / e.total) * valides.length));
        },
      });
      toast.success(`${data.count} photo${data.count > 1 ? 's' : ''} ajoutée${data.count > 1 ? 's' : ''} ✓`);
      onRefresh();
    } catch {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadCount(0);
      setUploadTotal(0);
      if (fileInputRef.current)   fileInputRef.current.value   = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // ── Gestion caméra ───────────────────────────────────────────────────────
  const ouvrirCamera = () => {
    if (isMobile()) {
      // Sur mobile : utiliser l'input capture natif (appareil photo de l'OS)
      cameraInputRef.current?.click();
    } else {
      // Sur desktop : ouvrir la modal webcam
      setWebcamOpen(true);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFileChange   = (e) => uploadPhotos(e.target.files);
  const handleCameraChange = (e) => uploadPhotos(e.target.files);
  const handleDrop         = (e) => { e.preventDefault(); setDragOver(false); uploadPhotos(e.dataTransfer.files); };

  const supprimerPhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/produits/photos/${photoId}`);
      toast.success('Photo supprimée');
      onRefresh();
    } catch { toast.error(t('commun.erreurServeur')); }
  };

  const definirPrincipale = async (photoId) => {
    try {
      await api.put(`/produits/photos/${photoId}/principale`);
      toast.success('Photo principale définie');
      onRefresh();
    } catch { toast.error(t('commun.erreurServeur')); }
  };

  const sauvegarderSeuil = async (varianteId) => {
    try {
      await api.put(`/produits/variantes/${varianteId}/seuil`, { seuil_alerte_min: parseInt(seuilVal) });
      toast.success('Seuil mis à jour');
      setSeuilEdit(null);
      onRefresh();
    } catch { toast.error(t('commun.erreurServeur')); }
  };

  const niveauVariante = (v) => {
    if (v.stock_disponible === 0) return 'red';
    if (v.stock_disponible <= v.seuil_alerte) return 'orange';
    return 'green';
  };

  const photos = (produit.photos || []).filter(p => p.id);

  return (
    <div className="space-y-6">

      {/* ── Photos ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
            {t('produits.photos')}
            {photos.length > 0 && (
              <span className="ml-2 text-gray-500 font-normal normal-case text-xs">
                ({photos.length} photo{photos.length > 1 ? 's' : ''})
              </span>
            )}
          </h3>

          {/* Boutons d'ajout */}
          <div className="flex items-center gap-2">
            {/* Galerie / fichiers */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Choisir dans la galerie"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-yellow-400 text-xs font-medium disabled:opacity-50 transition-colors"
            >
              <ImagePlus size={14} />
              <span className="hidden sm:inline">Galerie</span>
            </button>

            {/* Caméra */}
            <button
              onClick={ouvrirCamera}
              disabled={uploading}
              title={isMobile() ? 'Prendre une photo' : 'Utiliser la webcam'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-yellow-400 text-xs font-medium disabled:opacity-50 transition-colors"
            >
              <Camera size={14} />
              <span className="hidden sm:inline">{isMobile() ? 'Appareil photo' : 'Webcam'}</span>
            </button>
          </div>
        </div>

        {/* Inputs cachés */}
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" disabled={uploading} />
        {/* Input capture pour mobile — pas de multiple, ouvre l'appareil photo natif */}
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraChange} className="hidden" disabled={uploading} />

        {/* Zone de drop */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed transition-all mb-3 cursor-pointer select-none
            ${dragOver ? 'border-yellow-500 bg-yellow-500/10 scale-[1.01]' : 'border-gray-700 hover:border-gray-600'}
            ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center justify-center py-5 gap-2">
              <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-yellow-400 text-sm font-medium">Envoi en cours… {uploadCount}/{uploadTotal}</p>
            </div>
          ) : photos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-500">
              <Upload size={28} />
              <p className="text-sm">Glissez vos photos ici ou <span className="text-yellow-400 underline">parcourez</span></p>
              <p className="text-xs text-gray-600">JPG, PNG, WEBP · max 5 Mo/photo · jusqu'à 10 à la fois</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-600">ou</span>
                <button
                  onClick={(e) => { e.stopPropagation(); ouvrirCamera(); }}
                  className="flex items-center gap-1 text-xs text-yellow-500 hover:text-yellow-400"
                >
                  <Camera size={12} /> utilisez la caméra
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-2 gap-2 text-gray-600 text-xs hover:text-gray-500">
              <Upload size={13} />
              <span>Glissez d'autres photos ici</span>
            </div>
          )}
        </div>

        {/* Grille photos */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {photos.map(photo => (
              <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-800 shadow-md">
                <img
                  src={`${API_BASE}${photo.url}`}
                  alt=""
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />

                {/* Badge principale */}
                {photo.principale && (
                  <div className="absolute top-1.5 left-1.5 z-10">
                    <span className="bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 shadow">
                      <Star size={9} fill="black" /> Principale
                    </span>
                  </div>
                )}

                {/* Actions au survol */}
                <div className="absolute inset-0 bg-black/65 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  <div className="flex items-center gap-2">
                    {/* Agrandir */}
                    <button
                      onClick={() => setPhotoZoom(`${API_BASE}${photo.url}`)}
                      title="Agrandir"
                      className="p-1.5 bg-gray-700/80 text-white rounded-full hover:bg-gray-600 transition-colors"
                    >
                      <ZoomIn size={13} />
                    </button>

                    {/* Définir comme principale */}
                    {!photo.principale && (
                      <button
                        onClick={() => definirPrincipale(photo.id)}
                        title="Définir comme photo principale"
                        className="p-1.5 bg-yellow-500 text-black rounded-full hover:bg-yellow-400 transition-colors"
                      >
                        <Star size={13} />
                      </button>
                    )}

                    {/* Supprimer */}
                    <button
                      onClick={() => supprimerPhoto(photo.id)}
                      title="Supprimer"
                      className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-500 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Tuile "+" pour ajouter */}
            <button
              onClick={ouvrirCamera}
              disabled={uploading}
              className="aspect-square rounded-lg border-2 border-dashed border-gray-700 hover:border-yellow-600 flex flex-col items-center justify-center gap-1 text-gray-600 hover:text-yellow-500 transition-colors disabled:opacity-50"
              title="Ajouter une photo"
            >
              <Camera size={20} />
              <span className="text-xs">Photo</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Variantes et stock ── */}
      <div>
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-3">
          {t('produits.variantes')} & Stock
        </h3>
        <div className="space-y-2">
          {(produit.variantes || []).filter(v => v.id).map(v => (
            <div key={v.id} className="bg-gray-800 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-white font-medium text-sm">{v.modele}</span>
                    {v.taille_nom && <Badge variant="blue">{v.taille_nom}</Badge>}
                    <Badge variant="gray">{v.couleur}</Badge>
                    <Badge variant={niveauVariante(v)}>
                      {v.stock_disponible === 0 ? '❌ Rupture'
                        : v.stock_disponible <= v.seuil_alerte ? `⚠️ ${v.stock_disponible} restant(s)`
                        : `✅ ${v.stock_disponible} en stock`}
                    </Badge>
                  </div>
                  <p className="text-gray-500 text-xs font-mono">{v.code_variante}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-yellow-400 font-semibold text-sm">{formatMontant(v.prix_vente_magasin)}</p>
                  {v.prix_vente_livraison > 0 && v.prix_vente_livraison !== v.prix_vente_magasin && (
                    <p className="text-gray-500 text-xs">{formatMontant(v.prix_vente_livraison)} <span className="text-gray-600">(livraison)</span></p>
                  )}
                </div>
              </div>

              {/* Seuil alerte */}
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>Seuil d'alerte :</span>
                {seuilEdit === v.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number" min="0"
                      value={seuilVal === 0 || seuilVal === '0' ? '' : seuilVal}
                      onChange={e => setSeuilVal(e.target.value)}
                      placeholder="0"
                      className="w-16 bg-gray-700 border border-yellow-500 text-white rounded px-2 py-0.5 text-xs"
                    />
                    <button onClick={() => sauvegarderSeuil(v.id)} className="text-green-400 hover:text-green-300">
                      <CheckCircle size={14} />
                    </button>
                    <button onClick={() => setSeuilEdit(null)} className="text-gray-500 hover:text-gray-400">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setSeuilEdit(v.id); setSeuilVal(v.seuil_alerte || 3); }}
                    className="flex items-center gap-1 text-yellow-500 hover:text-yellow-400"
                  >
                    <span>{v.seuil_alerte || 3}</span>
                    <Edit size={10} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={onClose}>{t('commun.fermer')}</Button>
      </div>

      {/* ── Modal Webcam ── */}
      {webcamOpen && (
        <ModalWebcam
          onCapture={(files) => uploadPhotos(files)}
          onClose={() => setWebcamOpen(false)}
        />
      )}

      {/* ── Zoom photo ── */}
      {photoZoom && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 cursor-zoom-out"
          onClick={() => setPhotoZoom(null)}
        >
          <button className="absolute top-4 right-4 p-2 bg-gray-800/80 text-white rounded-full hover:bg-gray-700" onClick={() => setPhotoZoom(null)}>
            <X size={20} />
          </button>
          <img
            src={photoZoom}
            alt="Photo agrandie"
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
